const {loadGoogleMapsWithAddons} = require('od-modules/helpers/map/googleMapsLoader');
const MapEvents = require('od-modules/helpers/map/events');
const addOnsCns = require('od-modules/helpers/map/addons');
const {mapMarkers} = require('od-modules/helpers/map/markers');
const mapParser = {
    'accommodation_map_manager': require('od-modules/parsers/map/accommodation_map_manager'),
    'accommodation_map_full_view': require('od-modules/parsers/map/accommodation_map_full_view'),
};
const MAP_POSITION = {
    lat: 39.470239,
    lng: -0.376805,
};
const MAP_OPTIONS = {
    mockType: 'general_map',
    scrollwheel: false,
    backgroundColor: '#E7E7E7',
    mapId: '1d274f1764b4583',
};
const GM_CONTROL_CLASS = '.gmnoprint';
let libMap;

async function getDependenciesPromise() {
    libMap = await loadGoogleMapsWithAddons('markerCluster', 'infoBox');
}

function getMapOptions({mapConfig = {}}) {
    return Odigeo._.extend({}, MAP_OPTIONS, {
        center: new libMap.LatLng(MAP_POSITION.lat, MAP_POSITION.lng),
        mapTypeId: libMap.MapTypeId.ROADMAP,
        mapTypeControl: mapConfig.absConfig.hasTooltipTemplate,
        fullscreenControl: mapConfig.fullscreenControl,
    }, mapConfig.absConfig);
}

function createMap(cfg, options) {
    this.map = new libMap.Map(cfg.mapDiv, getMapOptions.call(this, options));
}

function createAddOns(cfg, options) {
    this.addOns = addOnsCns(this.map, libMap, Odigeo._.extend({
        template: options.hotelMapTemplate,
        parser: options.hotelMapParser,
    }, options.mapConfig));
    this.addOns.createHotelInfo();
    this.addOns.createBtnGroup({
        btnGroupCbk: cfg.btnGroupCbk,
    });
}

function createMarkersController({mapConfig = {}}) {
    this.markers = mapMarkers(this.map, mapConfig, libMap);
}

function cleanMap() {
    if (this.addOns) {
        if (this.addOns.isInfoWindowOpen()) {
            libMap.event.trigger(this.addOns.getHotelInfo().getInfoWindow(), 'closeWindow');
        }
        this.addOns.cleanMapCluster();
    }
    this.markers.cleanMarkers();
}

async function onClickMarker(evt, marker) {
    evt.stopPropagation();
    evt.preventDefault();

    await marker.fetchHotelDetails();
    openInfoWindow.call(this, marker);
    openWindowByClickOnMarker.call(this, marker);
}

function openWindowByClickOnMarker(marker) {
    libMap.event.trigger(
        this.addOns.getHotelInfo().getInfoWindow(),
        'openWindowByClickOnMarker', marker.dataMarker
    );
}

function openInfoWindow(marker) {
    this.hasUserInteraction = true;
    if (marker instanceof libMap.OverlayView) {
        this.addOns.getHotelInfo().toggle(marker, {
            addOns: this.addOns,
            ...marker.mapConfig,
        });
    }
}

function createMapMarkers(cfg) {
    this.markers.createMarkers({
        onClickMarker: onClickMarker.bind(this),
        ...cfg,
    });
}

function onClickCluster() {
    libMap.event.trigger(this.addOns.getHotelInfo().getInfoWindow(), 'clickCluster');
}

function createMapClusters({mapConfig = {}}) {
    this.addOns.createMapCluster(this.markers.getMarkerList());

    if (mapConfig.hasClustersLabeled) {
        this.addOns.setClustersLabeled();
    } else if (mapConfig.hasClustersRegularStyle) {
        this.addOns.setClustersRegularStyle();
    }

    if (mapConfig.absConfig.hasTopVisibilityMarkerPreselected) {
        this.addOns.excludeMarkerFromCluster(this.markers.getMarkerByPreselectedKey());
    }

    MapEvents.getListeners(libMap).listenTo(this.addOns.getMapCluster(), {
        'clusterclick': onClickCluster.bind(this),
    });
}

module.exports = class UiGoogleMap {
    constructor(mapConfig) {
        this.options = {
            mapConfig,
            galleryImageTemplate: mapConfig.templates.mapGalleryImage,
            hotelMapTemplate: mapConfig.templates[mapConfig.hotelMapTemplate],
            hotelMapParser: mapParser[mapConfig.hotelMapParser],
        };
        this.map = null;
        this.addOns = null;
        this.markers = null;
        this.hasUserInteraction = false;
        this.hasDragInteraction = false;
        this.isReady = getDependenciesPromise();
        this.events = Object.assign({}, MapEvents, {
            getListeners: () => MapEvents.getListeners(libMap),
        });
    }

    startMap(cfg) {
        createMap.call(this, cfg, this.options);
        createAddOns.call(this, cfg, this.options);
        createMarkersController.call(this, this.options);
    }

    refreshBounds() {
        libMap.event.trigger(this.addOns.getHotelInfo().getInfoWindow(), 'closeWindow');
        this.map.fitBounds(this.markers.getBounds());
    }

    closeHotelInfo() {
        libMap.event.trigger(this.addOns.getHotelInfo().getInfoWindow(), 'closeWindow');
    }

    refresh(cfg) {
        cleanMap.call(this);
        createMapMarkers.call(this, cfg);

        if (this.markers.hasMarkers()) {
            createMapClusters.call(this, this.options);
        }
        return this.markers.getMarkerList();
    }

    hasMarkers() {
        return this.markers.hasMarkers();
    }

    setHasUserInteraction(hasAction) {
        this.hasUserInteraction = hasAction;
    }

    setHasDragInteraction(hasAction) {
        this.hasDragInteraction = hasAction;
    }

    getInfoWindow() {
        return this.addOns.getHotelInfo().getInfoWindow();
    }

    hasHotelInfoElement(el) {
        return this.addOns.getHotelInfo().hasElement(el);
    }

    isInfoWindowOpen() {
        return this.addOns.isInfoWindowOpen();
    }

    async openInfoWindowByMarker(marker) {
        await marker.fetchHotelDetails();
        openInfoWindow.call(this, marker);
    }

    getMarkersPrevNext(key) {
        const visibleMarkers = this.markers.getVisibleMarkerList();
        const selectedMarker = this.markers.getMarkerByKey(key);
        const index = Odigeo._.findIndex(visibleMarkers, Odigeo._.partial(Odigeo._.isEqual, selectedMarker));

        return Odigeo.Utils.getPrevNextList(visibleMarkers, index);
    }

    setDataMarker(markerKey, dataKey, dataValue) {
        this.markers.getMarkerByKey(markerKey).setDataMarker(dataKey, dataValue);
    }

    pickDataMarker(markerKey, dataKey) {
        return Odigeo._.pick(this.markers.getMarkerByKey(markerKey).dataMarker, dataKey);
    }

    getMarkerByPreselectedKey() {
        return this.markers.getMarkerByPreselectedKey();
    }

    setGalleryImageMarker(markerKey, galleryOptions) {
        const marker = this.markers.getMarkerByKey(markerKey);

        marker.setGalleryImage(this.options.galleryImageTemplate(marker.dataMarker), galleryOptions);
    }

    getMarkerByKey(key) {
        return this.markers.getMarkerByKey(key);
    }

    centerMarkerPosition() {
        this.addOns.getHotelInfo().centerMapPosition();
    }

    centerHotelInfoPosition() {
        this.addOns.getHotelInfo().setFixedCornerPosition();
    }

    hasSelectedGoogleMapControls(el) {
        return Odigeo._.isElement($(el).closest(GM_CONTROL_CLASS).get(0));
    }

    hasSelectedMarkerCluster(el) {
        return $(el).hasClass(this.addOns.getClusterHook());
    }

    updateMarkerPrice(accommodationDealKey, price) {
        this.markers.updateMarkerPrice(accommodationDealKey, price);
    }

    resizeMap(parameters) {
        libMap.event.trigger(this.map, 'resize', parameters);
    }
};
